import React, { useEffect, useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap'
import { firestore } from "_utils/firebase-app";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";

import { CustomSpinner } from '_components/atoms';

import { useSelector, useDispatch } from "react-redux";
import { selectFirebaseInfo, selectFirebaseUser } from '_redux/slices/main-screen/user';
import { getFlightConversation, resetSendFlightMessageFlags, selectFlightMessages, selectSelectedFlightToChat, selectSendFlightMessageStatus, sendFlightMessage, setFlightMessages, setFlightMessagesToSeen } from '_redux/slices/timeline/flight-information-list';

import { TbChecks } from "react-icons/tb";
import { IoMdSend } from "react-icons/io";
import { formatNameForAvatar } from '_utils/functions';

export const getHoursAndMinutes = (date) => {
    let jsDate;

    if (typeof date === 'string') {
        jsDate = new Date(date);
    } else if (date && 'seconds' in date) {
        jsDate = new Date(date.seconds * 1000 + Math.floor(date.nanoseconds / 1000000));
    } else if (date && '_seconds' in date) {
        jsDate = new Date(date._seconds * 1000 + Math.floor(date._nanoseconds / 1000000));

    } else if (date instanceof Date) {
        jsDate = date;
    } else {
        jsDate = new Date();
    }

    const hour = jsDate.getHours();
    const minute = jsDate.getMinutes();

    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const Message = ({ msg, user }) => {
    return <div style={{ display: 'flex', marginBottom: "20px", alignItems: 'flex-end', justifyContent: msg?.senderId !== user?.uid ? 'flex-end' : 'flex-start' }}>
        {msg?.senderId == user?.uid &&
            <div style={{
                border: "1px solid",
                borderRadius: "40px",
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                padding: "4px 6px 4px 6px",
                marginInlineEnd: "5px",
            }}>
                <p style={{ margin: 0, width: "fit-content" }}>{formatNameForAvatar(msg?.senderName)}</p>
            </div>
        }
        <div style={{
            maxWidth: '70%',
            padding: 10,
            borderRadius: 16,
            backgroundColor: "lightgray",
            borderBottomRightRadius: msg?.senderId !== user?.uid ? 2 : 16,
            borderBottomLeftRadius: msg?.senderId !== user?.uid ? 16 : 2,

        }}>
            <p style={{ margin: 0, width: "fit-content" }}>{msg?.text ?? ""} </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: 'flex-end', gap: 5 }}>
                <p style={{ margin: 0, width: "fit-content" }}>{getHoursAndMinutes(msg?.sentAt ?? "")} </p>
                {msg?.senderId !== user?.uid && <TbChecks color={msg?.status === "seen" ? "green" : "gray"} />}
            </div>
        </div>
        {msg?.senderId !== user?.uid &&
            <div style={{
                border: "1px solid",
                borderRadius: "40px",
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                padding: "4px 6px 4px 6px",
                marginInlineStart: "5px",
            }}>
                <p style={{ margin: 0, width: "fit-content" }}>{formatNameForAvatar(msg?.senderName)}</p>
            </div>
        }

    </div>
}

const FlightChat = () => {

    const messagesEndRef = useRef(null);


    const dispatch = useDispatch();

    const selectedFlightToChat = useSelector(selectSelectedFlightToChat);
    const flightMessages = useSelector(selectFlightMessages);
    const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user
    const firebaseUser = useSelector(selectFirebaseUser);
    const sendFlightMessageStatus = useSelector(selectSendFlightMessageStatus);

    const [messageText, setMessageText] = useState("");
    const [disabledFlight, setDisabledFlights] = useState(false);
    const [isExistingFlightConv, setIsExistingFlightConv] = useState(false);       // check if the flight conversation already exists

    useEffect(() => {
        dispatch(resetSendFlightMessageFlags());
    }, []);

    useEffect(() => {
        if (sendFlightMessageStatus === "succeeded" || sendFlightMessageStatus === "failed") {
            dispatch(resetSendFlightMessageFlags());
        };
    }, [sendFlightMessageStatus]);

    useEffect(() => {
        let isFlightDisabled = false;
        if (selectedFlightToChat?.operationInfo?.time) {
            // for departure flights (5 minutes before), for arrival flights (1 hour before)
            const minusTime = selectedFlightToChat?.type === "departure"
                ? 1000 * 60 * 5
                : 1000 * 60 * 60;
            const currentTime = new Date(new Date().getTime() - minusTime);
            const operationTime = new Date(selectedFlightToChat?.operationInfo?.time);

            if (currentTime > operationTime) {
                isFlightDisabled = true;
            }
        }

        setDisabledFlights(isFlightDisabled);
    }, [selectedFlightToChat]);


    useEffect(() => {
        (async () => {
            if (selectedFlightToChat?._id) {
                const response = await dispatch(getFlightConversation(selectedFlightToChat?._id));
                if (response?.meta?.requestStatus === "fulfilled") {
                    if (Object.isObjectEmpty(response?.payload)) {
                        setIsExistingFlightConv(true);
                    } else {
                        setIsExistingFlightConv(false);
                    }

                } else {
                    setIsExistingFlightConv(false);
                }

            }

        })()
    }, [selectedFlightToChat]);

    useEffect(() => {
        if (selectedFlightToChat?._id) {
            const flightMessagesCollectionRef = collection(firestore, "flightsMessages");
            const flightMessagesQuery = query(
                flightMessagesCollectionRef,
                where("conversationId", "==", selectedFlightToChat?._id),
                orderBy("sentAt", "desc"),
                limit(50)
            );
            const unsubscribeMessages = onSnapshot(flightMessagesQuery, (querySnapshot) => {
                const messages = [];
                querySnapshot.forEach((doc) => {
                    const msgData = doc.data();
                    messages.push(msgData);
                });
                const unSeenMessages = messages?.filter((msg) => msg?.seenBy?.includes(firebaseUser?.uid));
                if (Array.isFullArray(unSeenMessages)) {
                    dispatch(setFlightMessagesToSeen({
                        userId: firebaseUser?.uid,
                        conversationId: selectedFlightToChat?._id
                    }))

                }

                dispatch(setFlightMessages(Array.isFullArray(messages) ? messages : []));

                setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            });
            return () => unsubscribeMessages();
        }
    }, []);


    const handleSendMessage = async () => {
        const payload = {

            iata: dbUser.details.authority,
            body: {
                conversationId: selectedFlightToChat?._id,
                senderId: firebaseUser?.uid,
                senderName: firebaseUser?.displayName,
                text: messageText,
                senderType: "portal"
            }

        };

        const res = await dispatch(sendFlightMessage(payload));

        if (res?.meta?.requestStatus === "fulfilled") {
            setMessageText("")
        }

    }

    return (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div style={{ maxHeight: "850px", height: "850px", overflowY: "scroll", paddingInline: "30px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }} >
                {flightMessages?.map((msg) => {
                    if ('type' in msg && msg.type === 'header') {
                        return (
                            <div style={{ display: "flex", alignItems: "center", margin: "0px 20px", justifyContent: "center" }}>
                                <p style={{ margin: 0, width: "fit-content" }}>{msg.date} </p>
                            </div>
                        );
                    }
                    return <Message msg={msg} user={firebaseUser} />;
                })}
                <div ref={messagesEndRef} />

            </div>
            {disabledFlight && <div style={{ display: "flex", justifyContent: "center", alignItems: "center", color: "red" }}>
                <p>{`This flight has ${selectedFlightToChat?.type === "departure" ? "DEPARTED" : "LANDED"}`}</p>
            </div>}

            {!disabledFlight && isExistingFlightConv && <div style={{ display: "flex", justifyContent: "center", alignItems: "center", color: "red" }}>
                <p>{`Non of the staff has assigned to this flight yet, please assign a staff to this flight to start the conversation`}</p>
            </div>}
            <Form.Group className="mt-5 d-flex" style={{ gap: 5 }}>
                <Form.Control as="textarea" rows={3} onChange={(e) => setMessageText(e.target.value)} value={messageText} disabled={disabledFlight} />
                <Button variant="outline-primary" style={{ height: 40 }} disabled={!messageText || disabledFlight} onClick={handleSendMessage}>
                    {sendFlightMessageStatus === "loading" ? <CustomSpinner size={"small"} /> : <IoMdSend />}
                </Button>
            </Form.Group>


        </div>
    )
}

export default FlightChat
